import { useEffect, useState } from "react";
import { Wallet, Download, ChevronDown, ChevronUp, CalendarCheck2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SlipRincian } from "@/components/SlipRincian";
import { api, apiErrorMessage, formatRupiah, formatTanggalID, downloadFile } from "@/lib/api";

export default function SlipGaji() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/slip/mine");
      setRows(data);
      if (data.length) setOpenId(data[0].id);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function unduh(slip) {
    setBusyId(slip.id);
    try {
      await downloadFile(
        `/payroll/slip/${slip.id}/pdf`,
        `SlipGaji_${(slip.nama || "").replace(/\s+/g, "_")}_${slip.periode}.pdf`
      );
      toast.success("Slip gaji diunduh");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-slip-gaji">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Kepegawaian</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
          Slip Gaji
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Rincian gaji Anda per periode. Slip hanya muncul setelah diterbitkan oleh Admin.
        </p>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-slate-500 py-14">Memuat slip gaji…</div>
      ) : rows.length === 0 ? (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="slip-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <Receipt size={22} />
          </div>
          <div className="mt-3 font-semibold text-slate-700">Belum ada slip gaji terbit</div>
          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Slip gaji akan tampil di sini setelah proses penggajian periode berjalan diterbitkan oleh Unit SDM &amp; Umum.
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((s) => {
            const terbuka = openId === s.id;
            return (
              <div key={s.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`slip-card-${s.id}`}>
                <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-11 h-11 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
                    <Wallet size={19} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-extrabold text-slate-900 leading-tight">
                      {s.periode_label}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <CalendarCheck2 size={12} /> Cutoff{" "}
                      {s.periode_rentang ||
                        `${formatTanggalID(s.periode_start)} – ${formatTanggalID(s.periode_end)}`}{" "}
                      · Dibayarkan {formatTanggalID(s.tanggal_gajian)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {s.hari_hadir} hari hadir · {s.total_jam_lembur} jam lembur
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Gaji Bersih</div>
                    <div className="text-xl font-extrabold text-teal-800" data-testid={`slip-netto-${s.id}`}>
                      {formatRupiah(s.netto)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenId(terbuka ? null : s.id)}
                      className="gap-1.5"
                      data-testid={`slip-toggle-${s.id}`}
                    >
                      {terbuka ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Rincian
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => unduh(s)}
                      disabled={busyId === s.id}
                      className="bg-teal-700 hover:bg-teal-800 gap-1.5"
                      data-testid={`slip-download-${s.id}`}
                    >
                      <Download size={13} /> {busyId === s.id ? "…" : "PDF"}
                    </Button>
                  </div>
                </div>

                {terbuka ? (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-5">
                    <SlipRincian slip={s} testId={`slip-detail-${s.id}`} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
